import { useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';

/**
 * Custom hook to sync Replan viewer state with URL
 * 
 * URL Format: /replan?project=ProjectName&version=VersionName&folder=/path&page=PageName
 * 
 * This enables:
 * - Deep linking to specific pages
 * - Shareable URLs
 * - Browser back/forward navigation
 * 
 * Performance optimizations:
 * - Uses refs to avoid unnecessary re-renders
 * - Debounces URL updates for rapid navigation
 * - Memoizes URL params to prevent unnecessary comparisons
 * - Avoids creating new objects/arrays in render
 */
export const useUrlSync = ({
  projectName,
  versionName,
  selectedFolder,
  currentPageName,
  pages,
  currentImageIndex,
  onProjectChange,
  onVersionChange,
  onFolderChange,
  goToPage,
  goToPageByName,
  pageRestoreSettled = true,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  // Refs for tracking state without causing re-renders
  const isSyncingFromUrl = useRef(false);
  const isInitialMount = useRef(true);
  const hasCompletedInitialSync = useRef(false);
  const updateTimeoutRef = useRef(null);
  const syncTargetRef = useRef(null); // Stores what URL params we're syncing to
  const isNavigating = useRef(false); // Prevents concurrent navigation actions
  
  // Store current state in ref to access in callbacks without stale closures
  const stateRef = useRef({ projectName, versionName, selectedFolder, currentPageName });
  stateRef.current = { projectName, versionName, selectedFolder, currentPageName };

  // Mirrors pageRestoreSettled so updateUrl (memoized) reads the fresh value.
  // While the saved-page restore is unresolved, the URL must not carry a page
  // param: the automatic first-page selection after a project switch would
  // otherwise be written as if it were a deliberate deep link, overriding the
  // restored page.
  const pageRestoreSettledRef = useRef(pageRestoreSettled);
  pageRestoreSettledRef.current = pageRestoreSettled;
  
  // Store previous URL to detect actual changes
  const prevUrlRef = useRef('');
  
  // Track previous state to detect what actually changed
  const prevStateRef = useRef({ projectName: '', versionName: '', selectedFolder: '', currentPageName: '' });

  // Memoize URL params parsing - only recalculate when searchParams changes
  const urlParams = useMemo(() => ({
    project: searchParams.get('project') || '',
    version: searchParams.get('version') || '',
    folder: searchParams.get('folder') || '',
    page: searchParams.get('page') || '',
  }), [searchParams]);

  // Memoize current state as URL string for comparison
  const currentStateAsUrl = useMemo(() => {
    const params = new URLSearchParams();
    if (projectName) params.set('project', projectName);
    if (versionName) params.set('version', versionName);
    if (selectedFolder) params.set('folder', selectedFolder);
    // A page name from another project must never leak into this project's URL,
    // and no page is written until the saved-page restore has settled.
    if (currentPageName && pageRestoreSettled && urlParams.project === projectName) {
      params.set('page', currentPageName);
    }
    return params.toString();
  }, [projectName, versionName, selectedFolder, currentPageName, urlParams.project, pageRestoreSettled]);

  /**
   * Debounced URL update - prevents rapid URL changes during fast navigation
   * Uses requestAnimationFrame for smooth updates.
   * Determines whether to push or replace based on the nature of the change.
   */
  const updateUrl = useCallback((options = {}) => {
    // Guard: Skip if already navigating (prevents cascade failures)
    if (isNavigating.current) return;
    
    // Clear any pending update
    if (updateTimeoutRef.current) {
      cancelAnimationFrame(updateTimeoutRef.current);
    }

    updateTimeoutRef.current = requestAnimationFrame(() => {
      const state = stateRef.current;
      const prevState = prevStateRef.current;
      const params = new URLSearchParams();

      if (state.projectName) params.set('project', state.projectName);
      if (state.versionName) params.set('version', state.versionName);
      if (state.selectedFolder) params.set('folder', state.selectedFolder);
      // Never write a page name from a different project into this project's URL,
      // and never write a page until the saved-page restore has settled.
      const currentUrlProject = new URLSearchParams(
        window.location.search,
      ).get('project');
      if (
        state.currentPageName &&
        pageRestoreSettledRef.current &&
        currentUrlProject === state.projectName
      ) {
        params.set('page', state.currentPageName);
      } else if (
        !pageRestoreSettledRef.current &&
        currentUrlProject === state.projectName
      ) {
        // While the restore is unresolved, preserve a page param that already
        // belongs to the current project (a genuine deep link) instead of
        // writing or stripping it.
        const existingPage = new URLSearchParams(
          window.location.search,
        ).get('page');
        if (existingPage) {
          params.set('page', existingPage);
        }
      }

      const newSearch = params.toString();
      // Use window.location.search for the freshest value
      const currentSearch = window.location.search.replace(/^\?/, '');

      // Only navigate if URL actually changed
      if (newSearch !== currentSearch && newSearch !== prevUrlRef.current) {
        prevUrlRef.current = newSearch;
        
        // Decision matrix for replace vs push:
        // - Initial mount/sync: always replace (don't create history for app initialization)
        // - Syncing from URL (back/forward): always replace (we're navigating to existing history)
        // - ALL other changes: PUSH (create new history entry for every navigation)
        const shouldReplace = 
          !hasCompletedInitialSync.current || // Still initializing
          isSyncingFromUrl.current; // Syncing from browser navigation
        
        // Update previous state for next comparison
        prevStateRef.current = { ...state };
        
        isNavigating.current = true;
        navigate(`${location.pathname}?${newSearch}`, { replace: shouldReplace });
        
        // Clear navigation flag after URL update completes
        setTimeout(() => { isNavigating.current = false; }, 50);
      }
    });
  }, [location.pathname, navigate]);
  // Removed location.search from dependencies

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (updateTimeoutRef.current) {
        cancelAnimationFrame(updateTimeoutRef.current);
      }
    };
  }, []);

  /**
   * Initialize state from URL on mount (runs once)
   * This loads any URL parameters into state and marks initialization as complete
   */
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlProject = params.get('project') || '';
    const urlVersion = params.get('version') || '';
    const urlFolder = params.get('folder') || '';
    
    if (urlProject || urlVersion || urlFolder) {
      // BUG FIX (Fix 1): versionName is the overlay-exclude signal and must
      // never be initialized from the URL version param, which exists only for
      // navigation (page targeting). Setting versionName here would exclude the
      // target version's pages, breaking getPageIndexInVersion and collapsing
      // per-page measurement data into index 0.
      syncTargetRef.current = {
        project: urlProject,
        // Intentionally omit version — versionName stays '' (no overlay exclusion).
        // URL version param is handled by the goToPageByName effect below.
        version: '',
        folder: urlFolder,
      };
      isSyncingFromUrl.current = true;
    }
    
    // Store initial state to avoid treating first load as a change
    if (urlProject) {
      onProjectChange(urlProject);
      prevStateRef.current.projectName = urlProject;
    }
    // NOTE: urlVersion is NOT passed to onVersionChange — see comment above.
    if (urlFolder) {
      onFolderChange(urlFolder);
      prevStateRef.current.selectedFolder = urlFolder;
    }
    
    // Mark initialization as complete after state has propagated
    // The sync flag will be cleared by the state update effect
    const timeoutId = setTimeout(() => {
      isInitialMount.current = false;
      hasCompletedInitialSync.current = true;
    }, 150); // Longer delay to ensure persistence loading doesn't interfere

    return () => clearTimeout(timeoutId);
  }, []); // Intentionally run only on mount

  /**
   * Navigate to page from URL after pages are loaded
   */
  useEffect(() => {
    if (!urlParams.page || pages.length === 0 || urlParams.page === currentPageName) return;
    // A page param is only valid for the project it appears with in the URL.
    // Ignore it when it refers to a different project (stale after a switch).
    if (urlParams.project && projectName && urlParams.project !== projectName) return;

    if (goToPageByName) {
      goToPageByName(urlParams.page);
    } else {
      const pageIndex = pages.findIndex(
        (p) => p.pageLabel === urlParams.page || p.name === urlParams.page
      );
      if (pageIndex !== -1 && pageIndex !== currentImageIndex) {
        goToPage(pageIndex);
      }
    }
  }, [urlParams.page, urlParams.project, projectName, pages, currentImageIndex, currentPageName, goToPage, goToPageByName]);

  /**
   * Update URL when state changes (user-initiated or persistence-loaded changes)
   * Uses memoized comparison to avoid unnecessary updates
   */
  useEffect(() => {
    // Don't update URL if there's no project
    if (!projectName) return;
    
    // If we're syncing from URL, check if we've reached the target state
    if (isSyncingFromUrl.current && syncTargetRef.current) {
      const reachedTarget = 
        projectName === syncTargetRef.current.project &&
        versionName === syncTargetRef.current.version &&
        selectedFolder === syncTargetRef.current.folder;
      
      if (reachedTarget) {
        // State fully synced with URL - safe to allow new URL updates
        isSyncingFromUrl.current = false;
        syncTargetRef.current = null;
        isNavigating.current = false; // Navigation complete
        prevStateRef.current = { projectName, versionName, selectedFolder, currentPageName };
        return; // Don't call updateUrl - URL is already correct
      } else {
        // Still syncing - don't trigger URL updates
        return;
      }
    }
    
    // Only update if the memoized state string differs from actual URL
    // Use window.location.search for the freshest value, not location from closure
    const currentSearch = window.location.search.replace(/^\?/, '');
    if (currentStateAsUrl !== currentSearch && currentStateAsUrl !== prevUrlRef.current) {
      updateUrl();
    }
  }, [currentStateAsUrl, projectName, versionName, selectedFolder, currentPageName, updateUrl]);
  // Removed location.search from dependencies to prevent stale closure issues

  /**
   * Handle browser back/forward navigation
   * Syncs state when URL params change externally (not from our updateUrl)
   */
  useEffect(() => {
    if (!hasCompletedInitialSync.current) return;
    
    // Guard: Skip if already navigating (debounce rapid back/forward clicks)
    if (isNavigating.current) return;

    const state = stateRef.current;
    const needsSync = 
      (urlParams.project && urlParams.project !== state.projectName) ||
      (urlParams.folder !== state.selectedFolder);

    if (needsSync) {
      // Validation: Skip if URL params look invalid (empty strings after trim)
      if (urlParams.project && !urlParams.project.trim()) {
        return; // Ignore invalid/malicious URLs
      }
      // Store the target params we're syncing to
      // NOTE: urlParams.version is intentionally NOT synced to versionName —
      // versionName is the overlay-exclude signal and must not come from URL.
      syncTargetRef.current = {
        project: urlParams.project,
        version: '',
        folder: urlParams.folder,
      };
      
      isSyncingFromUrl.current = true;

      if (urlParams.project !== state.projectName) {
        onProjectChange(urlParams.project);
      }
      if (urlParams.folder !== state.selectedFolder) {
        onFolderChange(urlParams.folder);
      }

      // The flag will be reset by the state update effect once state matches syncTargetRef
      // This prevents race conditions where the flag is reset before state updates complete
    }
  }, [urlParams.project, urlParams.version, urlParams.folder, onProjectChange, onVersionChange, onFolderChange]);

  return {
    urlParams,
    updateUrl,
  };
};

export default useUrlSync;
